import React from "react";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";

interface PackageHeaderProps {
  name: string;
  description: string;
  dependencies: string[];
  peerDependencies: string[];
}

const Container = styled.div`
  margin-bottom: ${spacing[12]};
`;

const PackageName = styled.h1`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.bold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.lg};
  color: ${text.secondary};
  margin: 0 0 ${spacing[8]} 0;
  line-height: 1.5;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
`;

const BadgeLabel = styled.span`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  margin-right: ${spacing[2]};
`;

const Badge = styled.span<{ $variant?: "internal" | "peer" }>`
  display: inline-block;
  padding: ${spacing[1]} ${spacing[3]};
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  border-radius: ${borderRadius.base};
  background-color: ${({ $variant }) =>
    $variant === "internal" ? interactive.default : surface.muted};
  color: ${({ $variant }) =>
    $variant === "internal" ? text.onDark : text.secondary};
`;

const PackageHeader: React.FC<PackageHeaderProps> = ({
  name,
  description,
  dependencies,
  peerDependencies,
}) => {
  return (
    <Container>
      <PackageName>{name}</PackageName>
      <Description>{description}</Description>
      {dependencies.length > 0 && (
        <BadgeRow>
          <BadgeLabel>Depends on:</BadgeLabel>
          {dependencies.map((dep) => (
            <Badge key={dep} $variant="internal">
              {dep}
            </Badge>
          ))}
        </BadgeRow>
      )}
      {peerDependencies.length > 0 && (
        <BadgeRow style={{ marginTop: spacing[2] }}>
          <BadgeLabel>Peer deps:</BadgeLabel>
          {peerDependencies.map((dep) => (
            <Badge key={dep} $variant="peer">
              {dep}
            </Badge>
          ))}
        </BadgeRow>
      )}
    </Container>
  );
};

export default PackageHeader;
